// ============================================================================
// BESPOKIAN Health - FAQ Page
// Accordion FAQ with luxury styling
// ============================================================================

import { useState } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Link } from '@tanstack/react-router';
import { ChevronDown, Phone, Mail, MessageCircle } from 'lucide-react';

const faqCategories = [
  {
    title: 'Pricing & Payment',
    faqs: [
      {
        question: 'How does BESPOKIAN pricing work?',
        answer: 'Our pricing model is designed for maximum transparency. We aim to charge the actual drug acquisition cost plus a 15% markup (capped at $300) and a flat $5 dispensing fee. By removing hidden Pharmacy Benefit Manager (PBM) markups, we provide a clear breakdown of how your price is calculated for every medication.',
      },
      {
        question: 'Do you accept insurance?',
        answer: 'We are working to secure contracts with most major insurance plans. Our platform is built to automatically compare your insurance copay against our transparent cash price, highlighting whichever option offers you the greatest savings.',
      },
      {
        question: 'Why is your cash price often lower than insurance?',
        answer: 'Traditional pharmacies often operate through PBMs that add multiple layers of cost. Bespokian Health is designed to skip these middlemen entirely, passing the direct savings to you. Our goal is to provide a transparent model that consistently outperforms traditional insurance copays.',
      },
    ],
  },
  {
    title: 'Shipping & Delivery',
    faqs: [
      {
        question: 'How long does shipping take?',
        answer: 'Standard shipping is typically 2-3 business days. We plan to offer free standard shipping on orders over $50, with express next-day delivery options available for a flat fee. Cold-chain medications are shipped in validated, temperature-controlled packaging to ensure clinical integrity.',
      },
      {
        question: 'Do you ship controlled substances?',
        answer: 'Where permitted by state and federal law, we can dispense and ship certain controlled substances upon receipt of a valid, verifiable prescription. These orders require adult signature confirmation and may be subject to additional identity verification steps to ensure patient safety and regulatory compliance.',
      },
    ],
  },
  {
    title: 'Prescriptions',
    faqs: [
      {
        question: 'Can I transfer my prescription from another pharmacy?',
        answer: 'Yes. Once you provide the name of your current pharmacy and the medication details, our team coordinates the transfer. This process typically completes within 24–48 hours, depending on the response time of the transferring pharmacy.',
      },
      {
        question: 'How do I get a new prescription?',
        answer: 'Your physician can route prescriptions directly to us via certified e-prescribing software. Additionally, you may access integrated telemedicine services to consult with licensed providers who can evaluate and prescribe medications for appropriate conditions.',
      },
      {
        question: 'Do you offer auto-refills?',
        answer: 'We offer an automated refill program for eligible maintenance medications. Our system is designed to notify you before a refill is processed, allowing you to skip or cancel at any time to ensure you only receive the medication you need.',
      },
    ],
  },
  {
    title: 'Safety & Licensing',
    faqs: [
      {
        question: 'Are you a licensed pharmacy?',
        answer: 'Bespokian Health is currently in the licensing phase and intends to be fully licensed to dispense in all 50 states. All medications are dispensed from facilities that meet strict state and federal regulatory standards, and every prescription is reviewed by a licensed pharmacist. We maintain rigorous HIPAA-compliant protocols to protect your health data.',
      },
      {
        question: 'What if I have questions about my medication?',
        answer: 'Licensed pharmacists are available to answer questions regarding your medications, potential drug interactions, side effects, and proper usage. You can reach our clinical team through our secure chat feature or via our dedicated clinical support line.',
      },
    ],
  },
];

// Helper function to calculate word count and reading time
function getReadingStats(text: string) {
  const words = text.trim().split(/\s+/).filter(word => word.length > 0).length;
  const readingTime = Math.max(1, Math.ceil(words / 200)); // 200 words per minute
  return { words, readingTime };
}

export function FAQPage() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('faq-content');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1">
        {/* Hero Section with Chemistry Lab Background */}
        <section
          className="relative h-screen flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 21, 58, 0.55), rgba(0, 21, 58, 0.9)), url("/img/heroes/health-faq-hero.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center center'
          }}
        >
          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-8" />
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-6 md:mb-8 leading-tight tracking-tight">
              FAQ
            </h1>
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-12" />

            {/* Learn More - Scroll to content section */}
            <div className="max-w-xl mx-auto">
              <div
                id="learn-more"
                role="button"
                tabIndex={0}
                onClick={scrollToContent}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') scrollToContent();
                }}
                className="bg-white/10 backdrop-blur-md rounded-lg px-6 py-4 border border-white/20 shadow-xl ring-1 ring-white/10 cursor-pointer hover:bg-white/20 transition-all duration-300 w-full"
                aria-label="Learn more about FAQs"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="text-left">
                    <div className="text-white/90 text-xs uppercase tracking-widest mb-2">
                      LEARN MORE
                    </div>
                    <div className="text-white/70 text-lg">
                      See how it works
                    </div>
                  </div>
                  <MessageCircle className="h-6 w-6 text-white/80 flex-shrink-0" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Content Section */}
        <div id="faq-content">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Gold accent line */}
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Help Center
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              Frequently Asked Questions
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              Find answers to common questions about BESPOKIAN Health. 
              Can't find what you're looking for? Our team is here to help.
            </p>
          </div>
        </section>

        {/* FAQ Accordion Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {faqCategories.map((category, categoryIndex) => (
              <div key={category.title} className={categoryIndex > 0 ? 'mt-12' : ''}>
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-0.5 bg-gold-500" />
                  <h2 className="font-display text-xl text-navy-800 tracking-tight">{category.title}</h2>
                </div>

                {/* FAQ Items */}
                <div className="space-y-3">
                  {category.faqs.map((faq, faqIndex) => {
                    const key = `${categoryIndex}-${faqIndex}`;
                    const isOpen = openItems[key];
                    const { words, readingTime } = getReadingStats(faq.answer);

                    return (
                      <div
                        key={key}
                        className={`border rounded-xl overflow-hidden transition-colors ${
                          isOpen ? 'border-gold-300 bg-gold-50/30' : 'border-navy-100 hover:border-navy-200'
                        }`}
                      >
                        <button
                          className="w-full px-6 py-4 text-left"
                          onClick={() => toggleItem(key)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-navy-800 block">{faq.question}</span>
                              {/* Mobile: Show stats under question */}
                              <div className="lg:hidden mt-2">
                                <span className="text-xs text-navy-400">
                                  {words} words • {readingTime} min read
                                </span>
                              </div>
                            </div>
                            {/* Desktop: Show stats next to question */}
                            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
                              <span className="text-xs text-navy-400 whitespace-nowrap">
                                {words} words • {readingTime} min read
                              </span>
                              <ChevronDown
                                className={`h-5 w-5 text-navy-400 transition-transform flex-shrink-0 ${
                                  isOpen ? 'transform rotate-180 text-gold-600' : ''
                                }`}
                              />
                            </div>
                            {/* Mobile: Show chevron */}
                            <ChevronDown
                              className={`lg:hidden h-5 w-5 text-navy-400 transition-transform flex-shrink-0 mt-0.5 ${
                                isOpen ? 'transform rotate-180 text-gold-600' : ''
                              }`}
                            />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <p className="text-navy-600 leading-relaxed">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-navy-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">Still Have Questions?</h2>
              <p className="text-navy-600">Our team is ready to help you.</p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              {/* Phone */}
              <a
                href="tel:1-877-237-7679"
                className="bg-white rounded-xl p-6 text-center shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-sage-600" />
                </div>
                <h3 className="font-semibold text-navy-800 mb-1">Call Us</h3>
                <p className="text-navy-600 text-sm mb-2">Pharmacist Support</p>
                <p className="text-gold-600 font-medium">1-877-BES-PORX</p>
              </a>

              {/* Email */}
              <a
                href="mailto:hello@bespokianhealth.com"
                className="bg-white rounded-xl p-6 text-center shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="w-12 h-12 bg-gold-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-gold-600" />
                </div>
                <h3 className="font-semibold text-navy-800 mb-1">Email Us</h3>
                <p className="text-navy-600 text-sm mb-2">Response within 24 hours</p>
                <p className="text-gold-600 font-medium">hello@bespokianhealth.com</p>
              </a>

              {/* Chat */}
              <Link
                to="/login"
                className="bg-white rounded-xl p-6 text-center shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="w-12 h-12 bg-navy-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-6 w-6 text-navy-600" />
                </div>
                <h3 className="font-semibold text-navy-800 mb-1">Live Chat</h3>
                <p className="text-navy-600 text-sm mb-2">Chat with our team</p>
                <p className="text-gold-600 font-medium">Start Chat</p>
              </Link>
            </div>
          </div>
        </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

